/**
 * Request Access Page
 * Página para solicitação de acesso à plataforma
 */

import { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Link,
  Alert,
  Stack,
  Grid,
} from '@mui/material';
import { ArrowBack, CheckCircle } from '@mui/icons-material';
import { supabase } from '../../lib/supabase';
import { touchFriendlyButton, touchFriendlyTextField } from '../../utils/mobileFriendlyStyles';
import AppFooter from '../AppFooter';

export function RequestAccessPage() {
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [empresaCargo, setEmpresaCargo] = useState('');
  const [motivo, setMotivo] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validações básicas
    if (!nome.trim() || !email.trim() || !motivo.trim()) {
      setError('Por favor, preencha todos os campos obrigatórios');
      setLoading(false);
      return;
    }

    // Validação de email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Por favor, insira um email válido');
      setLoading(false);
      return;
    }

    try {
      // Inserir solicitação na tabela access_requests
      const { error: insertError } = await supabase
        .from('access_requests')
        .insert([
          {
            nome_completo: nome.trim(),
            email: email.trim().toLowerCase(),
            empresa_cargo: empresaCargo.trim() || null,
            motivo_acesso: motivo.trim(),
            status: 'pending',
          },
        ]);

      if (insertError) {
        // Verificar se é erro de email duplicado
        if (insertError.code === '23505') {
          setError('Este email já possui uma solicitação cadastrada. Verifique sua caixa de entrada ou aguarde a aprovação.');
        } else {
          setError(insertError.message || 'Erro ao enviar solicitação');
        }
        setLoading(false);
        return;
      }

      // Sucesso
      setSuccess(true);
      
      // Redirecionar para login após 5 segundos
      setTimeout(() => {
        navigate('/login');
      }, 5000);
    } catch (err) {
      console.error('Erro ao solicitar acesso:', err);
      setError('Erro ao enviar solicitação. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          background: '#121212',
        }}
      >
        <Box component="main" sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
        <Container maxWidth="lg">
          <Grid container spacing={0} sx={{ minHeight: { md: '80vh' } }}>
            {/* Branding à esquerda */}
            <Grid item xs={12} md={6} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', p: { xs: 4, md: 6 } }}>
              <Box textAlign="center">
                <Box component="img" src="/setima-logo.svg" alt="Sétima" sx={{ width: { xs: 180, md: 240 }, height: 'auto', mb: 3 }} />
                <Typography variant="h4" sx={{ fontWeight: 300, color: 'rgba(255,255,255,0.92)' }}>
                  Solicitação enviada com sucesso
                </Typography>
                <Typography variant="body1" sx={{ mt: 2, color: 'rgba(255,255,255,0.8)' }}>
                  Você será redirecionado para o login em instantes.
                </Typography>
              </Box>
            </Grid>
            {/* Card à direita */}
            <Grid item xs={12} md={6} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: { xs: 3, md: 4 } }}>
              <Paper elevation={8} sx={{ p: { xs: 3, md: 5 }, borderRadius: 3, backgroundColor: 'rgba(255,255,255,0.98)', maxWidth: 520, width: '100%' }}>
                <Stack spacing={3} alignItems="center">
                  <CheckCircle sx={{ fontSize: 64, color: 'success.main' }} />
                  <Typography variant="h5" align="center">
                    Solicitação Enviada com Sucesso!
                  </Typography>
                  <Alert severity="success" sx={{ width: '100%' }}>
                    <Typography variant="body2" paragraph>
                      Sua solicitação de acesso foi recebida e está aguardando aprovação do administrador.
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Você receberá um email quando seu acesso for liberado.
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      Redirecionando para login em 5 segundos...
                    </Typography>
                  </Alert>
                  <Button
                    component={RouterLink}
                    to="/login"
                    variant="contained"
                    fullWidth
                    sx={{
                      ...touchFriendlyButton,
                      py: 1.5,
                      fontWeight: 600,
                      textTransform: 'none',
                      background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #16213e 0%, #0f1419 100%)',
                      },
                    }}
                  >
                    Ir para Login
                  </Button>
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Container>
        </Box>
        <AppFooter mode="dark" />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: '#121212',
      }}
    >
      <Box component="main" sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
      <Container maxWidth="lg">
        <Grid container spacing={0} sx={{ minHeight: { md: '80vh' } }}>
          {/* Branding à esquerda */}
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              p: { xs: 4, md: 6 },
              color: 'white',
            }}
          >
            <Box sx={{ mb: 6 }}>
              <Box component="img" src="/setima-logo.svg" alt="Sétima" sx={{ width: { xs: 180, md: 240 }, height: 'auto' }} />
            </Box>
            <Typography variant="h3" sx={{ fontWeight: 300, color: 'rgba(255,255,255,0.92)', textAlign: 'center', mb: 2 }}>
              Solicitar Acesso — Plataforma Sétima
            </Typography>
            <Typography variant="body1" sx={{ textAlign: 'center', maxWidth: 560, color: 'rgba(255,255,255,0.82)' }}>
              Preencha o formulário. Sua solicitação será analisada e você receberá um email quando for aprovada.
            </Typography>
          </Grid>

          {/* Formulário à direita */}
          <Grid item xs={12} md={6} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: { xs: 3, md: 4 } }}>
            <Paper elevation={8} sx={{ p: { xs: 3, md: 5 }, borderRadius: 3, backgroundColor: 'rgba(255,255,255,0.98)', maxWidth: 520, width: '100%' }}>
              {/* Voltar */}
              <Button component={RouterLink} to="/login" startIcon={<ArrowBack />} sx={{ alignSelf: 'flex-start', mb: 1 }}>
                Voltar para Login
              </Button>
              
              {/* Título */}
              <Typography variant="h5" component="h1" gutterBottom sx={{ fontWeight: 600, textAlign: 'center' }}>
                Solicitar Acesso
              </Typography>
              <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
                Campos marcados com * são obrigatórios
              </Typography>

              {error && (
                <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <form onSubmit={handleSubmit}>
                <Stack spacing={2.5}>
                  <TextField label="Nome Completo *" type="text" value={nome} onChange={(e) => setNome(e.target.value)} fullWidth required disabled={loading} sx={touchFriendlyTextField} />
                  <TextField label="Email *" type="email" value={email} onChange={(e) => setEmail(e.target.value)} fullWidth required disabled={loading} sx={touchFriendlyTextField} helperText="Use um email válido." />
                  <TextField label="Empresa/Cargo" type="text" value={empresaCargo} onChange={(e) => setEmpresaCargo(e.target.value)} fullWidth disabled={loading} sx={touchFriendlyTextField} placeholder="Ex: Sétima - Analista Financeiro" />
                  <TextField label="Motivo do Acesso *" multiline rows={4} value={motivo} onChange={(e) => setMotivo(e.target.value)} fullWidth required disabled={loading} sx={touchFriendlyTextField} placeholder="Descreva brevemente o motivo do acesso..." helperText="Mínimo 20 caracteres" error={motivo.length > 0 && motivo.length < 20} />
                  
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    fullWidth
                    disabled={loading || (motivo.length > 0 && motivo.length < 20)}
                    sx={{
                      ...touchFriendlyButton,
                      py: 1.5,
                      fontWeight: 600,
                      textTransform: 'none',
                      background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #16213e 0%, #0f1419 100%)',
                      },
                    }}
                  >
                    {loading ? 'Enviando...' : 'Solicitar Acesso'}
                  </Button>
                </Stack>
              </form>

              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Já tem acesso?{' '}
                  <Link component={RouterLink} to="/login" underline="hover">
                    Faça login
                  </Link>
                </Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>
        </Container>
      </Box>
      <AppFooter mode="dark" />
    </Box>
  );
}

export default RequestAccessPage;
